import { makeAutoObservable, reaction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import { getDateRange, getLastWorkday, getNextWorkday, uniqueArray, utils } from "@intrasync/utils";

import ProductionScheduleRightHeaderCell from "./ProductionScheduleCells/ProductionSchedule.RightHeaderCell";
import ProductionScheduleRightBodyCell from "./ProductionScheduleCells/ProductionSchedule.RightBodyCell";
import ProductionScheduleLeftHeaderCell from "./ProductionScheduleCells/ProductionSchedule.LeftHeaderCell";
import ProductionScheduleLeftBodyCell from "./ProductionScheduleCells/ProductionSchedule.LeftBodyCell";

interface PanelEntity {
  [key: string]: string;
}

// Placeholder departments for demo
export const PLACEHOLDER_DEPARTMENTS = [
  { id: "dept-1", name: "Department 1" },
  { id: "dept-2", name: "Department 2" },
  { id: "dept-3", name: "Department 3" },
  { id: "dept-4", name: "Department 4" },
];

export type ViewMode = "byMold" | "byDepartment";

class ProductionScheduleStore {
  rootStore: RootStore;

  showMu = false;

  // View mode toggle: 'byMold' (current default) or 'byDepartment'
  viewMode: ViewMode = "byMold";

  currentlySelectedEntities: number[] = [];

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    reaction(
      () =>
        this.entities.map((entity) => {
          return { ...entity };
        }),
      () => {
        if (!this.productionScheduleActive) return null;
        if (this.rootStore.scheduleGridStore?.namespaces?.ProductionSchedule) {
          this.rootStore.scheduleGridStore?.namespaces?.ProductionSchedule.recomputeScheduler();
        }
      }
    );

    // Recompute when view mode changes
    reaction(
      () => this.viewMode,
      () => {
        if (!this.productionScheduleActive) return null;
        if (this.rootStore.scheduleGridStore?.namespaces?.ProductionSchedule) {
          this.rootStore.scheduleGridStore?.namespaces?.ProductionSchedule.recomputeScheduler();
        }
      }
    );
  }

  // View mode actions
  setViewMode = (mode: ViewMode) => {
    this.viewMode = mode;
  };

  toggleViewMode = () => {
    this.viewMode = this.viewMode === "byMold" ? "byDepartment" : "byMold";
  };

  toggleShowMu = () => {
    this.showMu = !this.showMu;
  };

  setCurrentlySelectedEntities = (entityId: number) => {
    const foundIndex = this.currentlySelectedEntities.findIndex(
      (currentEntityId) => currentEntityId === entityId
    );

    console.log("foundIndex", foundIndex);

    if (foundIndex !== -1) {
      this.currentlySelectedEntities = this.currentlySelectedEntities.filter(
        (currentEntityId) => currentEntityId !== entityId
      );

      return;
    }

    this.currentlySelectedEntities = [
      ...this.currentlySelectedEntities,
      entityId,
    ];
  };

  moveCurrentlySelectedEntities = (direction: "up" | "down") => {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    if (!panelStore) {
      return;
    }
    const currentlySelectedEntities: PanelEntity[] = this.entities.filter(
      (entity: PanelEntity) =>
        this.currentlySelectedEntities.includes(entity.id as unknown as number)
    );

    if (currentlySelectedEntities.length === 0) {
      return;
    }

    const groupedByDate = utils.groupBy(
      currentlySelectedEntities,
      "castingDate"
    );
    const sortedDates = Object.keys(groupedByDate).sort();

    if (direction === "up") {
      sortedDates.forEach((date) => {
        const currentEntities = groupedByDate[date];
        const dateBefore = getLastWorkday(date);
        const includedIds = currentEntities.map((entity) => entity.id);
        console.log("includedIds", includedIds);

        panelStore?.patch(
          null,
          {
            castingDate: dateBefore,
          },
          {
            query: {
              id: {
                $in: includedIds,
              },
            },
          }
        );
      });
    } else {
      sortedDates.forEach((date) => {
        const currentEntities = groupedByDate[date];
        const dateAfter = getNextWorkday(date as string);
        const includedIds = currentEntities.map((entity) => entity.id);
        console.log("includedIds", includedIds);

        panelStore?.patch(
          null,
          {
            castingDate: dateAfter,
          },
          {
            query: {
              id: {
                $in: includedIds,
              },
            },
          }
        );
      });
    }
  };

  clearCurrentlySelectedEntities = () => {
    this.currentlySelectedEntities = [];
  };

  getRowHeight = (index: number): number => {
    const modifiedIndex = index - 1;
    if (this.dateRange.length === 0) {
      return 50;
    }
    const date = this.dateRange[modifiedIndex];

    // Use different grouping based on view mode
    if (this.viewMode === "byDepartment") {
      const dateEntities = this.entitiesWithDepartment.filter(
        (entity) => entity.castingDate === date
      );
      const deptCounts: { [deptId: string]: number } = {};
      dateEntities.forEach((entity) => {
        const deptId = entity.departmentId;
        if (deptId) {
          deptCounts[deptId] = (deptCounts[deptId] || 0) + 1;
        }
      });

      let maxCount = 0;
      for (const deptId in deptCounts) {
        if (deptCounts[deptId] > maxCount) {
          maxCount = deptCounts[deptId];
        }
      }

      if (maxCount >= 2 && maxCount <= 4) {
        return maxCount * 52;
      }
      if (maxCount > 4) {
        return maxCount * 51;
      }
      return 54;
    }

    // Default: by mold view
    const dateEntities = this.entities.filter(
      (entity) => entity.castingDate === date
    );
    const moldCounts: { [moldNumber: string]: number } = {};
    dateEntities.forEach((entity) => {
      const moldNumber = entity.moldNumber as string;
      if (moldNumber) {
        moldCounts[moldNumber] = (moldCounts[moldNumber] || 0) + 1;
      }
    });

    let maxCount = 0;

    for (const moldNumber in moldCounts) {
      if (moldCounts[moldNumber] > maxCount) {
        maxCount = moldCounts[moldNumber];
      }
    }

    if (maxCount >= 2 && maxCount <= 4) {
      const rowHeight = maxCount * 52;
      return rowHeight;
    }
    if (maxCount > 4) {
      const rowHeight = maxCount * 51;
      return rowHeight;
    }
    return 54;
  };

  get currentEntitiesMolds() {
    return utils.sortByMold(
      uniqueArray(
        this.entities.map((entity) => entity.moldNumber as string)
      ) as string[]
    );
  }

  // Assign entities to placeholder departments based on panel ID (for demo)
  getDepartmentForEntity = (entity: PanelEntity): string => {
    const entityId = parseInt(entity.id as string, 10) || 0;
    const deptIndex = entityId % PLACEHOLDER_DEPARTMENTS.length;
    return PLACEHOLDER_DEPARTMENTS[deptIndex].id;
  };

  // Get entities enriched with department ID for the department view
  get entitiesWithDepartment(): (PanelEntity & { departmentId: string })[] {
    return this.entities.map((entity) => ({
      ...entity,
      departmentId: this.getDepartmentForEntity(entity),
    }));
  }

  get columnHeaders() {
    if (this.viewMode === "byDepartment") {
      return this.departmentColumnHeaders;
    }
    return this.moldColumnHeaders;
  }

  get moldColumnHeaders() {
    const headersByMold = this.currentEntitiesMolds
      .filter((mold) => {
        if (!mold) {
          return false;
        }
        if (mold === "0") {
          return false;
        }
        if (mold.includes("MU")) {
          return false;
        }
        if (mold.includes("FBF")) {
          return false;
        }
        return true;
      })
      .map((mold) => {
        return {
          width: 230,
          title: mold,
          key: mold,
          headerKey: "moldNumber",
          component: ProductionScheduleRightHeaderCell,
          bodyComponent: ProductionScheduleRightBodyCell,
        };
      });

    return [
      {
        width: 130,
        title: "Date",
        key: "castingDate",
        component: ProductionScheduleLeftHeaderCell,
        bodyComponent: ProductionScheduleLeftBodyCell,
      },
      ...headersByMold,
    ];
  }

  get departmentColumnHeaders() {
    const departmentColumns = PLACEHOLDER_DEPARTMENTS.map((dept) => ({
      width: 300,
      title: dept.name,
      key: dept.id,
      headerKey: "departmentId",
      departmentId: dept.id,
      component: ProductionScheduleRightHeaderCell,
      bodyComponent: ProductionScheduleRightBodyCell,
    }));

    return [
      {
        width: 130,
        title: "Date",
        key: "castingDate",
        component: ProductionScheduleLeftHeaderCell,
        bodyComponent: ProductionScheduleLeftBodyCell,
      },
      ...departmentColumns,
    ];
  }

  get entities(): PanelEntity[] {
    const routerStore = this.rootStore.routerStore;
    const panelStore = this.rootStore.panelStore as ServiceStore;
    if (!routerStore) return [];
    const { params } = routerStore;
    if (!params?.jobNumber) return [];
    const currentEntities = panelStore.entities
      .filter((entity) => {
        if (!entity.jobNumber) {
          return false;
        }
        return utils.isSameJobNumber(
          entity.jobNumber as number,
          params?.jobNumber as number
        );
      })
      .filter((entity) => {
        if (!entity.castingDate) {
          return false;
        }
        return entity.castingDate !== "1970-01-01";
      })
      .filter((entity) => {
        if (!entity.moldNumber) {
          return false;
        }
        return entity.moldNumber !== "0";
      })
      .filter((entity) => {
        if (
          !this.showMu &&
          entity?.moldNumber &&
          typeof entity.moldNumber === "string" &&
          (entity?.moldNumber?.includes("MU") ||
            entity?.moldNumber?.includes("FBF"))
        ) {
          return false;
        }
        return true;
      });

    // If in department view, enrich entities with departmentId
    if (this.viewMode === "byDepartment") {
      return currentEntities.map((entity) => ({
        ...entity,
        departmentId: this.getDepartmentForEntity(entity as PanelEntity),
      })) as PanelEntity[];
    }

    return currentEntities as PanelEntity[];
  }

  get dateRange() {
    if (this.entities.length === 0) {
      return [];
    }
    const dates = this.entities
      .filter(
        (entity) => entity.castingDate && entity.castingDate !== "1970-01-01"
      )
      .map((entity) => entity.castingDate as string)
      .sort();

    const startDate = dates[0];
    const endDate = dates[dates.length - 1];

    if (!startDate || !endDate) {
      return [];
    }

    return getDateRange(startDate, endDate, { weekdays: true });
  }

  get productionScheduleActive() {
    return this.rootStore.scheduleGridStore?.namespaces?.ProductionSchedule
      ? true
      : false;
  }
}

export default ProductionScheduleStore;
